import { useState } from 'react'
import { PlayerDataItem } from '@/modules/manage-player/type'
import { columns } from '../PlayerDataTable/constants'
import { DataTableArrow } from '@/common/ui/table/data-table-arrow'
import { Card } from '@/common/ui/card'
import { Separator } from '@/common/ui/separator'
import { DateRangePicker } from '@/common/components/DateRangePicker'
import DateRangeButtons from '@/common/components/DateRangeButtons'
import { Button } from '@/common/ui/button'
import { ArrowLeft, Download } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import Heading from '@/common/shared/heading'
import PlayerSearchControls from '@/common/components/PlayerSearchControls'
import { ExportCsvAmountModal } from '@/common/components/ExportCsvAmountModal'
import { useMutationExportPlayerDataListCsv } from '@/modules/manage-player/api/hooks/useMutationExportPlayerDataListCsv'
import type { ExportPlayerDataListCsvRequest } from '@/modules/manage-player/type'
import { SortOrder } from '@/schema'

type SortOrderOrUndefined = SortOrder | undefined

interface PlayerDataListingProps {
	data: PlayerDataItem[]
	total: number
	isLoading?: boolean
	onSort?: (columnId: string, order: SortOrderOrUndefined) => void
	showBackButton?: boolean
	exportParams?: Omit<
		ExportPlayerDataListCsvRequest,
		'take' | 'skip' | 'filename' | 'columnMappings'
	>
}

export default function PlayerDataListing({
	data,
	total,
	isLoading,
	onSort,
	showBackButton = false,
	exportParams,
}: PlayerDataListingProps) {
	const navigate = useNavigate()
	const exportCsvMutation = useMutationExportPlayerDataListCsv()
	const [showExportModal, setShowExportModal] = useState(false)

	const handleExportCsv = (amount: number) => {
		exportCsvMutation.mutate({
			...exportParams,
			take: amount || total || 1000,
			skip: 0,
			filename: 'player_data_list',
			columnMappings: {
				phone: 'เบอร์โทรศัพท์',
				username: 'ชื่อผู้ใช้',
				totalDepositAmount: 'ยอดฝากทั้งหมด (บาท)',
				totalWithdrawAmount: 'ยอดถอนทั้งหมด (บาท)',
				totalProfit: 'กำไรทั้งหมด (บาท)',
				totalWinloss: 'W/L (บาท)',
				lastOnline: 'ใช้งานล่าสุด',
			},
		})
	}

	return (
		<Card>
			<div className="space-y-4 p-3 sm:p-4">
				{showBackButton && (
					<Button
						onClick={() => navigate(-1)}
						variant="outline"
						size="sm"
						className="mb-4 flex items-center gap-2"
					>
						<ArrowLeft className="h-4 w-4" />
						Back
					</Button>
				)}

				{/* Header Section - Mobile Responsive */}
				<div className="space-y-4">
					{/* Title and Date Controls Row */}
					<div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
						{/* Title */}
						<Heading
							title={`ข้อมูลรายได้ผู้เล่น`}
							description="ข้อมูลรายได้ผู้เล่น"
						/>

						{/* Date Controls and Export */}
						<div className="flex flex-col gap-3 sm:items-end">
							<div className="flex flex-col gap-2 sm:flex-row sm:items-center">
								<Button
									variant="outline"
									size="sm"
									onClick={() => setShowExportModal(true)}
									disabled={exportCsvMutation.isPending || !data.length}
									className="flex items-center gap-2"
								>
									<Download className="h-4 w-4" />
									<span className="sm:inline">โหลด CSV</span>
								</Button>
								<DateRangePicker />
							</div>
							<DateRangeButtons />
						</div>
					</div>

					{/* Search Controls */}
					<PlayerSearchControls />
				</div>

				<Separator className="my-4 sm:my-6" />

				{/* Table Section */}
				<div className="space-y-4">
					<DataTableArrow
						columns={columns}
						data={data}
						total={total}
						isLoading={isLoading}
						onSort={onSort}
					/>
				</div>
			</div>
			<ExportCsvAmountModal
				open={showExportModal}
				onOpenChange={setShowExportModal}
				onConfirm={handleExportCsv}
				defaultAmount={total || 1000}
				maxAmount={100000}
				isLoading={exportCsvMutation.isPending}
			/>
		</Card>
	)
}
